/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { deviceIntegrationsDeleteConfig } from "../funcs/deviceIntegrationsDeleteConfig.js";
import { deviceIntegrationsGetConfig } from "../funcs/deviceIntegrationsGetConfig.js";
import { deviceIntegrationsGetCoverage } from "../funcs/deviceIntegrationsGetCoverage.js";
import { deviceIntegrationsListManagedDevices } from "../funcs/deviceIntegrationsListManagedDevices.js";
import { deviceIntegrationsListProviders } from "../funcs/deviceIntegrationsListProviders.js";
import { deviceIntegrationsListSchedules } from "../funcs/deviceIntegrationsListSchedules.js";
import { deviceIntegrationsRetrySchedule } from "../funcs/deviceIntegrationsRetrySchedule.js";
import { deviceIntegrationsSetScheduleEnabled } from "../funcs/deviceIntegrationsSetScheduleEnabled.js";
import { deviceIntegrationsTestConnection } from "../funcs/deviceIntegrationsTestConnection.js";
import { deviceIntegrationsUpsertConfig } from "../funcs/deviceIntegrationsUpsertConfig.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { DeviceIntegrationConfig } from "../models/components/deviceintegrationconfig.js";
import { DeviceIntegrationCoverage } from "../models/components/deviceintegrationcoverage.js";
import { DeviceIntegrationScheduleState } from "../models/components/deviceintegrationschedulestate.js";
import { DeviceIntegrationTestConnectionResult } from "../models/components/deviceintegrationtestconnectionresult.js";
import { ListDeviceIntegrationProvidersResult } from "../models/components/listdeviceintegrationprovidersresult.js";
import { ListDeviceIntegrationSchedulesResult } from "../models/components/listdeviceintegrationschedulesresult.js";
import {
  DeleteDeviceIntegrationConfigRequest,
  DeleteDeviceIntegrationConfigSecurity,
} from "../models/operations/deletedeviceintegrationconfig.js";
import {
  GetDeviceIntegrationConfigRequest,
  GetDeviceIntegrationConfigSecurity,
} from "../models/operations/getdeviceintegrationconfig.js";
import {
  GetDeviceIntegrationCoverageRequest,
  GetDeviceIntegrationCoverageSecurity,
} from "../models/operations/getdeviceintegrationcoverage.js";
import {
  ListDeviceIntegrationProvidersRequest,
  ListDeviceIntegrationProvidersSecurity,
} from "../models/operations/listdeviceintegrationproviders.js";
import {
  ListDeviceIntegrationSchedulesRequest,
  ListDeviceIntegrationSchedulesSecurity,
} from "../models/operations/listdeviceintegrationschedules.js";
import {
  ListManagedDevicesRequest,
  ListManagedDevicesResponse,
  ListManagedDevicesSecurity,
} from "../models/operations/listmanageddevices.js";
import {
  RetryDeviceIntegrationScheduleRequest,
  RetryDeviceIntegrationScheduleSecurity,
} from "../models/operations/retrydeviceintegrationschedule.js";
import {
  SetDeviceIntegrationScheduleEnabledRequest,
  SetDeviceIntegrationScheduleEnabledSecurity,
} from "../models/operations/setdeviceintegrationscheduleenabled.js";
import {
  TestDeviceIntegrationConnectionRequest,
  TestDeviceIntegrationConnectionSecurity,
} from "../models/operations/testdeviceintegrationconnection.js";
import {
  UpsertDeviceIntegrationConfigRequest,
  UpsertDeviceIntegrationConfigSecurity,
} from "../models/operations/upsertdeviceintegrationconfig.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class DeviceIntegrations extends ClientSDK {
  /**
   * deleteConfig deviceIntegrations
   *
   * @remarks
   * Disconnect the org-wide device integration for a provider. Synced inventory becomes invisible to coverage; reconnecting starts fresh.
   */
  async deleteConfig(
    request: DeleteDeviceIntegrationConfigRequest,
    security?: DeleteDeviceIntegrationConfigSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(deviceIntegrationsDeleteConfig(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getConfig deviceIntegrations
   *
   * @remarks
   * Get the org-wide device integration config for a provider. Returns an empty config (enabled=false, has_credentials=false) when none is set.
   */
  async getConfig(
    request: GetDeviceIntegrationConfigRequest,
    security?: GetDeviceIntegrationConfigSecurity | undefined,
    options?: RequestOptions,
  ): Promise<DeviceIntegrationConfig> {
    return unwrapAsync(deviceIntegrationsGetConfig(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getCoverage deviceIntegrations
   *
   * @remarks
   * Summarize agent coverage across the org's connected MDM inventories, optionally scoped to one provider.
   */
  async getCoverage(
    request?: GetDeviceIntegrationCoverageRequest | undefined,
    security?: GetDeviceIntegrationCoverageSecurity | undefined,
    options?: RequestOptions,
  ): Promise<DeviceIntegrationCoverage> {
    return unwrapAsync(deviceIntegrationsGetCoverage(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listManagedDevices deviceIntegrations
   *
   * @remarks
   * Page through the org's synced MDM device inventory, newest first, with each device's coverage bucket.
   */
  async listManagedDevices(
    request?: ListManagedDevicesRequest | undefined,
    security?: ListManagedDevicesSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PageIterator<ListManagedDevicesResponse, { cursor: string }>> {
    return unwrapResultIterator(deviceIntegrationsListManagedDevices(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listProviders deviceIntegrations
   *
   * @remarks
   * List the providers the device integrations framework supports, including the credential fields each needs.
   */
  async listProviders(
    request?: ListDeviceIntegrationProvidersRequest | undefined,
    security?: ListDeviceIntegrationProvidersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListDeviceIntegrationProvidersResult> {
    return unwrapAsync(deviceIntegrationsListProviders(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listSchedules deviceIntegrations
   *
   * @remarks
   * List the sync schedules and their state for a provider's org-wide device integration config. Returns an empty list when no config is set.
   */
  async listSchedules(
    request: ListDeviceIntegrationSchedulesRequest,
    security?: ListDeviceIntegrationSchedulesSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListDeviceIntegrationSchedulesResult> {
    return unwrapAsync(deviceIntegrationsListSchedules(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * retrySchedule deviceIntegrations
   *
   * @remarks
   * Make one sync schedule due immediately, lifting any automatic pause and resetting its failure streak.
   */
  async retrySchedule(
    request: RetryDeviceIntegrationScheduleRequest,
    security?: RetryDeviceIntegrationScheduleSecurity | undefined,
    options?: RequestOptions,
  ): Promise<DeviceIntegrationScheduleState> {
    return unwrapAsync(deviceIntegrationsRetrySchedule(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setScheduleEnabled deviceIntegrations
   *
   * @remarks
   * Enable or disable one sync schedule of a provider's device integration. Disabling records user intent on the schedule; only re-enabling clears it — config saves do not.
   */
  async setScheduleEnabled(
    request: SetDeviceIntegrationScheduleEnabledRequest,
    security?: SetDeviceIntegrationScheduleEnabledSecurity | undefined,
    options?: RequestOptions,
  ): Promise<DeviceIntegrationScheduleState> {
    return unwrapAsync(deviceIntegrationsSetScheduleEnabled(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * testConnection deviceIntegrations
   *
   * @remarks
   * Probe the provider with the stored credentials to verify they work. Save the config first; this tests what is stored, so secrets never round-trip.
   */
  async testConnection(
    request: TestDeviceIntegrationConnectionRequest,
    security?: TestDeviceIntegrationConnectionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<DeviceIntegrationTestConnectionResult> {
    return unwrapAsync(deviceIntegrationsTestConnection(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * upsertConfig deviceIntegrations
   *
   * @remarks
   * Create or update the org-wide device integration config for a provider. Omit credentials to keep the stored secrets; supplying credentials rotates them in place and resets the schedules' sync state. Settings merge per key: an omitted key keeps its stored value, a supplied key overwrites it.
   */
  async upsertConfig(
    request: UpsertDeviceIntegrationConfigRequest,
    security?: UpsertDeviceIntegrationConfigSecurity | undefined,
    options?: RequestOptions,
  ): Promise<DeviceIntegrationConfig> {
    return unwrapAsync(deviceIntegrationsUpsertConfig(
      this,
      request,
      security,
      options,
    ));
  }
}
